import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  ISiteFacet,
  useSiteFacets,
} from "../../../../redux/api/site-classification/siteClassificationAPI";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import DataTable from "../../../../shared/oversight-core/ui-elements/data-table/data-table";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import SettingsTableCard from "../settings-table-card/settings-table-card";
import AddSiteFacetModal from "./add-site-facet-modal";
import EditSiteFacetModal from "./edit-site-facet-modal";

interface IProps {
  // The organization whose vocabulary this is; absent for a platform admin.
  tenantId?: string;
}

const valuesSummary = (facet: ISiteFacet) => {
  const active = facet.values.filter((value) => !value.archived);

  if (active.length === 0) {
    return <span className="text-light">No values yet</span>;
  }

  const shown = active.slice(0, 4).map((value) => value.label);

  return `${shown.join(", ")}${active.length > shown.length ? ` +${active.length - shown.length} more` : ""}`;
};

// The organization's facets for classifying its sites, and their picklists. Tags describe a site;
// they never change who can see it.
const SiteClassification = (props: IProps) => {
  const { tenantId } = props;
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string>();
  const { data, isFetching, refetch } = useSiteFacets(tenantId);

  if (!tenantId) {
    return (
      <div className="text-light font-size-14">
        Site classification is set per organization. Sign in as an
        organization administrator to manage it.
      </div>
    );
  }

  const facets = data?.siteFacets ?? [];
  const editing = facets.find((facet) => facet.id === editingId);

  return (
    <>
      <SpinnerModal show={isFetching && !data} />
      <Row className="align-items-center mb-3">
        <Col className="text-light font-size-13">
          Facets describe your sites - their sector, region, the people they
          serve - so they can be filtered and reported on. They never change
          who can see a site.
        </Col>
        <Col className="col-auto">
          <AppButton
            text="Add facet"
            size="medium"
            variant="blue"
            iconName="add"
            onClick={() => setShowAdd(true)}
          />
        </Col>
      </Row>
      <SettingsTableCard>
        <DataTable
          headers={[
            { id: 1, columnName: "Facet" },
            { id: 2, columnName: "Values" },
            { id: 3, columnName: "Per site" },
            { id: 4, columnName: "Guests" },
            { id: 5, columnName: "" },
          ]}
          data={facets.map((facet) => ({
            data: [
              <div key="facet" className="font-size-13">
                <span className="text-dark font-weight-600">{facet.name}</span>
                {facet.required && (
                  <span className="text-light"> · required</span>
                )}
                {facet.archived && (
                  <span className="text-light"> · archived</span>
                )}
              </div>,
              <div key="values" className="font-size-13">
                {valuesSummary(facet)}
              </div>,
              <div key="cardinality" className="font-size-13 text-nowrap">
                {facet.cardinality === "SINGLE" ? "One" : "Several"}
              </div>,
              <div key="guests" className="font-size-13 text-nowrap">
                {facet.hiddenFromGuests ? "Hidden" : "Visible"}
              </div>,
              <div key="edit" className="d-flex justify-content-end">
                <AppButton
                  text="Edit"
                  size="extra-small"
                  variant="transparentWithBorder"
                  onClick={() => setEditingId(facet.id)}
                />
              </div>,
            ],
          }))}
        />
      </SettingsTableCard>
      <AddSiteFacetModal
        show={showAdd}
        tenantId={tenantId}
        onClose={() => setShowAdd(false)}
      />
      <EditSiteFacetModal
        show={!!editing}
        tenantId={tenantId}
        facet={editing}
        onClose={() => setEditingId(undefined)}
        onConflict={refetch}
      />
    </>
  );
};

export default SiteClassification;
